'use client';

import React, { Suspense, lazy } from 'react';
import { notFound } from 'next/navigation';
import Link from 'next/link';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { allLocaties, getLocatie } from '@/data/locatiesData';
import { trackCityCTAClick, trackDiagnosisRequest } from '@/lib/analytics';
import { MobileOnly, DesktopOnly } from '@/components/responsive/DeviceLayouts';
import {
  getCanonicalLocationPath,
  resolveLocationSlug,
} from '@/lib/locationAliases';

// Dynamic import for heavy below-fold component
const SiteInternalLinks = lazy(() => import('@/components/SiteInternalLinks'));

const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://roetfilter-center.be';

const relatedServices = [
  { href: '/roetfilter-reinigen', label: 'Roetfilter Reinigen', icon: '🔧' },
  { href: '/roetfilter-revisie', label: 'Roetfilter Revisie', icon: '⚙️' },
  { href: '/roetfilter-vervangen', label: 'Roetfilter Vervangen', icon: '🔄' },
  { href: '/adblue-problemen', label: 'AdBlue Problemen', icon: '💧' },
  { href: '/egr-klep-problemen', label: 'EGR-klep Problemen', icon: '🌀' },
  { href: '/katalysator-problemen', label: 'Katalysator Problemen', icon: '🏭' },
  { href: '/afgekeurd-voor-roet', label: 'Afgekeurd voor Roet?', icon: '🚨' },
];

// Urgency language variants per province/region for top 20 underperforming cities
function getUrgencyLabel(city: string, province: string): string {
  const brussels = ['Anderlecht', 'Brussel', 'Etterbeek', 'Evere', 'Ixelles', 'Jette', 'Molenbeek', 'Schaarbeek', 'Ukkel', 'Woluwe-Saint-Lambert'];
  const antwerpen = ['Antwerpen', 'Mechelen', 'Boom', 'Bornem', 'Brasschaat', 'Duffel', 'Edegem', 'Geel', 'Heist-op-den-Berg', 'Herentals', 'Kontich', 'Lier', 'Mol', 'Mortsel', 'Turnhout', 'Willebroek'];
  if (brussels.includes(city)) return 'Diagnose vandaag — LEZ-compliant';
  if (antwerpen.includes(city)) return 'Diagnose vandaag — snel geholpen';
  return 'Diagnose vandaag — zelfde dag';
}

function getUrgencySubtext(city: string, driveTime: string): string {
  return `Slechts ${driveTime} van ${city} — bel nu voor een afspraak vandaag`;
}

// Sidebar skeleton for lazy loading
function SidebarSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="bg-gray-200 rounded-2xl h-64" />
      <div className="bg-gray-200 rounded-2xl h-48" />
      <div className="bg-gray-200 rounded-2xl h-40" />
    </div>
  );
}

export default function CityPageClient({ slug }: { slug: string }) {
  const loc = getLocatie(slug);
  if (!loc) notFound();

  const nearbyCities =
    loc.nearbyCityLinks
      .map((slug) =>
        allLocaties.find(
          (location) =>
            location.slug ===
            resolveLocationSlug(slug)
        )
      )
      .filter(Boolean) as typeof allLocaties;

  const canonicalPath = getCanonicalLocationPath(loc.slug);
  const urgencyLabel = getUrgencyLabel(loc.city, loc.province);
  const urgencySubtext = getUrgencySubtext(loc.city, loc.driveTime);

  // JSON-LD: Service schema — Lokeren is the provider, city is areaServed only
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Roetfilter Reinigen in ${loc.city}`,
    description: `Professionele roetfilter reiniging, roetfilter diagnose en revisie voor klanten uit ${loc.city} en omgeving. Uitgevoerd door Roetfilter Center in Lokeren.`,
    url: `${baseUrl}${canonicalPath}`,
    provider: {
      '@type': 'AutoRepair',
      name: 'Roetfilter Center',
      url: baseUrl,
      telephone: '+32498545474',
      email: 'info@roetfilter-center.be',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Dijkstraat 22',
        addressLocality: 'Lokeren',
        postalCode: '9160',
        addressRegion: 'Oost-Vlaanderen',
        addressCountry: 'BE',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 51.1033, longitude: 3.9937 },
      openingHours: 'Mo-Fr 08:00-18:30, Sa 09:00-17:30',
      priceRange: '€225–€750',
    },
    areaServed: [
      { '@type': 'City', name: loc.city, containedInPlace: { '@type': 'AdministrativeArea', name: loc.province } },
      ...loc.nearbyTowns.map((t) => ({ '@type': 'City', name: t })),
    ],
    serviceType: 'Roetfilter Reiniging / Roetfilter Reinigen',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '195',
      highPrice: '750',
      priceCurrency: 'EUR',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Locaties', item: `${baseUrl}/locaties` },
      { '@type': 'ListItem', position: 3, name: `Roetfilter Reinigen ${loc.city}`, item: `${baseUrl}${canonicalPath}` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: loc.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <>
      <Header />
      <main>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

        {/* ── Breadcrumb navigation ── */}
        <nav className="bg-secondary/30 border-b border-border py-3" aria-label="Breadcrumb">
          <div className="rc-container">
            <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              <li>
                <Link href="/" className="hover:text-primary transition-colors font-medium">Home</Link>
              </li>
              <li aria-hidden="true" className="text-border mx-1">›</li>
              <li>
                <Link href="/locaties" className="hover:text-primary transition-colors font-medium">Locaties</Link>
              </li>
              <li aria-hidden="true" className="text-border mx-1">›</li>
              <li>
                <span className="text-primary font-semibold" aria-current="page">
                  Roetfilter Reinigen {loc.city}
                </span>
              </li>
            </ol>
          </div>
        </nav>

        {/* ── Hero — CTAs above fold, phone primary, offerte secondary ── */}
        <section className="bg-gradient-to-br from-primary to-red-800 text-white">
          <MobileOnly>
            <div className="px-4 py-12">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 bg-accent text-foreground text-xs font-extrabold px-3 py-1.5 rounded-full shadow-md animate-pulse">
                  🔴 {urgencyLabel}
                </span>
                <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  {loc.province} — {loc.driveTime} van Lokeren
                </span>
              </div>
              <h1 className="text-[30px] font-black leading-[1.05] tracking-[-0.03em] mb-3">
                {loc.h1}
              </h1>
              <p className="text-sm text-white/90 mb-2">{urgencySubtext}</p>
              <p className="text-sm text-white/75 mb-6">{loc.intro}</p>
              <div className="flex flex-col gap-3">
                <a
                  href="tel:0498545474"
                  className="flex w-full items-center justify-center gap-2 bg-accent text-foreground px-6 py-4 rounded-full font-extrabold text-sm shadow-lg"
                  onClick={() => trackCityCTAClick('phone', loc.slug, loc.city, 'hero')}
                >
                  📞 BEL NU: 0498 54 54 74
                </a>
                <Link
                  href="/offerte"
                  className="flex w-full items-center justify-center gap-2 bg-white text-primary px-6 py-4 rounded-full font-extrabold text-sm shadow-md"
                  onClick={() => trackCityCTAClick('offerte', loc.slug, loc.city, 'hero')}
                >
                  📋 Gratis Offerte Aanvragen
                </Link>
                <a
                  href={`https://wa.me/32498545474?text=Hallo%2C%20ik%20kom%20uit%20${encodeURIComponent(loc.city)}%20en%20heb%20een%20vraag%20over%20mijn%20roetfilter.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 bg-green-500 text-white px-6 py-4 rounded-full font-bold text-sm"
                  onClick={() => trackCityCTAClick('whatsapp', loc.slug, loc.city, 'hero')}
                >
                  💬 Stuur een bericht
                </a>
              </div>
              <p className="text-white/60 text-xs mt-4">
                ✓ Gratis diagnose bij herstelling &nbsp;·&nbsp; ✓ 1 jaar garantie &nbsp;·&nbsp; ✓ Alle merken
              </p>
            </div>
          </MobileOnly>

          <DesktopOnly>
            <div className="max-w-7xl mx-auto px-8 py-20 xl:px-10 xl:py-24">
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="inline-flex items-center gap-1.5 bg-accent text-foreground text-xs font-extrabold px-3 py-1.5 rounded-full shadow-md animate-pulse">
                  🔴 {urgencyLabel}
                </span>
                <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  {loc.province} — {loc.driveTime} van Lokeren
                </span>
              </div>
              <h1 className="text-[46px] font-black leading-[0.98] tracking-[-0.045em] mb-3 max-w-4xl xl:text-[56px]">
                {loc.h1}
              </h1>
              <p className="text-lg text-white/90 max-w-2xl mb-2">{urgencySubtext}</p>
              <p className="text-base text-white/75 max-w-2xl mb-8">{loc.intro}</p>
              <div className="flex flex-wrap gap-4 mb-6">
                <a
                  href="tel:0498545474"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-foreground px-8 py-4 rounded-full font-extrabold text-lg hover:opacity-90 transition-opacity shadow-xl ring-2 ring-white/30"
                  onClick={() => trackCityCTAClick('phone', loc.slug, loc.city, 'hero')}
                >
                  📞 BEL NU: 0498 54 54 74
                </a>
                <Link
                  href="/offerte"
                  className="inline-flex items-center justify-center gap-2 bg-white text-primary px-8 py-4 rounded-full font-extrabold text-base hover:bg-white/90 transition-colors shadow-lg"
                  onClick={() => trackCityCTAClick('offerte', loc.slug, loc.city, 'hero')}
                >
                  📋 Gratis Offerte Aanvragen
                </Link>
                <a
                  href={`https://wa.me/32498545474?text=Hallo%2C%20ik%20kom%20uit%20${encodeURIComponent(loc.city)}%20en%20heb%20een%20vraag%20over%20mijn%20roetfilter.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-500 text-white px-6 py-4 rounded-full font-bold text-sm hover:bg-green-600 transition-colors"
                  onClick={() => trackCityCTAClick('whatsapp', loc.slug, loc.city, 'hero')}
                >
                  💬 Stuur een bericht
                </a>
              </div>
              <p className="text-white/60 text-xs">
                ✓ Gratis diagnose bij herstelling &nbsp;·&nbsp; ✓ 1 jaar garantie &nbsp;·&nbsp; ✓ Zelfde dag afspraak mogelijk &nbsp;·&nbsp; ✓ Alle merken
              </p>
            </div>
          </DesktopOnly>
        </section>

        {/* ── Trust bar ── */}
        <section className="bg-foreground text-white py-4">
          <div className="rc-container">
            <div className="flex flex-wrap justify-center gap-6 text-sm font-semibold">
              <span>✓ 12 jaar ervaring</span>
              <span>✓ 1 jaar garantie</span>
              <span>✓ Gratis diagnose bij herstelling</span>
              <span>✓ Officiële emissietest</span>
              <span>✓ Alle merken en modellen</span>
            </div>
          </div>
        </section>

        {/* ── Main content + sidebar ── */}
        <section className="rc-section bg-white">
          <div className="rc-container">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

              {/* ── Left: main content (2/3) ── */}
              <div className="lg:col-span-2 space-y-10">

                {/* Problem section */}
                <div>
                  <h2 className="text-3xl font-extrabold text-foreground mb-6">
                    Roetfilter Problemen in {loc.city}
                  </h2>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {loc.problemParagraph}
                  </p>
                  <h3 className="text-xl font-extrabold text-foreground mb-4">
                    Symptomen die u niet mag negeren
                  </h3>
                  <ul className="space-y-2 mb-6">
                    {[
                      'Roetfilter waarschuwingslampje brandt op het dashboard',
                      'Merkbaar vermogensverlies bij optrekken of inhalen',
                      'Hoger brandstofverbruik dan normaal',
                      'Motor gaat in noodloop of stottert',
                      'Foutcodes P2002, P2463 of andere roetfilter codes',
                      'Afgekeurd bij autokeuring door te hoge roetuitstoot',
                      'Witte of blauwe rook uit de uitlaat',
                    ].map((s) => (
                      <li key={s} className="flex items-start gap-2 text-sm text-foreground">
                        <span className="text-primary font-bold mt-0.5">⚠</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="bg-accent/20 rounded-xl p-5 border border-accent/30">
                    <p className="text-sm font-bold text-foreground mb-1">
                      🚗 Bereikbaarheid vanuit {loc.city}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Slechts <strong>{loc.driveTime} rijden</strong> via de {loc.highway} naar Dijkstraat 22, 9160 Lokeren.
                      Ook goed bereikbaar vanuit {loc.nearbyTowns.slice(0, 3).join(', ')}.
                    </p>
                  </div>
                </div>

                {/* Why Lokeren */}
                <div className="rc-card-soft p-6 lg:p-7">
                  <h2 className="text-2xl font-extrabold text-foreground mb-4">
                    Waarom kiezen klanten uit {loc.city} voor Roetfilter Center?
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {loc.whyLokeren}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {loc.trustParagraph}
                  </p>
                </div>

                {/* Pricing */}
                <div className="rc-card p-6 lg:p-7 border-primary/20 bg-primary/5">
                  <h3 className="font-extrabold text-foreground mb-4">
                    Indicatieve Prijzen (losse roetfilter)
                  </h3>
                  {[
                    ["Reiniging personenwagen", '€225 – €275'],
                    ['Reiniging Jeep / SUV', '€245 – €295'],
                    ['Reiniging kleine bestelwagen', '€275 – €325'],
                    ['Reiniging grote bestelwagen', '€295 – €345'],
                    ['Reiniging trucks / bussen', '€490 – €750'],
                    ['Revisie', 'Vanaf €595'],
                    ['Diagnose', '€75 (terugbetaald bij herstelling)'],
                  ].map(([label, price]) => (
                    <div key={label} className="flex justify-between py-2 border-b border-border last:border-0">
                      <span className="text-sm font-medium text-foreground">{label}</span>
                      <span className="font-extrabold text-sm text-foreground">{price}</span>
                    </div>
                  ))}
                </div>

                {/* Related services */}
                <div>
                  <h2 className="text-2xl font-extrabold text-foreground mb-6">
                    Onze Diensten voor Klanten uit {loc.city}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { href: '/roetfilter-reinigen', title: 'Roetfilter Reinigen', desc: 'Professionele roetfilter reiniging met waterdruk en gespecialiseerde producten. Inclusief voor- en nameting.', icon: '🔧' },
                      { href: '/roetfilter-revisie', title: 'Roetfilter Revisie', desc: 'Volledige roetfilter herstelling voor ernstig beschadigde filters. Inclusief garantie.', icon: '⚙️' },
                      { href: '/roetfilter-vervangen', title: 'Roetfilter Vervangen', desc: 'Vervanging met kwaliteitsonderdelen wanneer reiniging niet meer volstaat.', icon: '🔄' },
                      { href: '/adblue-problemen', title: 'AdBlue Problemen', desc: 'SCR-systeem diagnose en herstelling. AdBlue-storingen opgelost.', icon: '💧' },
                      { href: '/egr-klep-problemen', title: 'EGR-klep Problemen', desc: 'EGR-klep reiniging of vervanging. Vermindert uitstoot en verbetert motorprestaties.', icon: '🌀' },
                      { href: '/katalysator-problemen', title: 'Katalysator Problemen', desc: 'Katalysator reiniging, diagnose en vervanging. Emissietest inbegrepen.', icon: '🏭' },
                    ].map((s) => (
                      <Link
                        key={s.href}
                        href={s.href}
                        className="rc-card p-5 lg:p-6 hover:border-primary hover:shadow-md transition-all group flex gap-4 items-start"
                        onClick={() => trackDiagnosisRequest(loc.slug, loc.city, 'services-section')}
                      >
                        <span className="text-2xl">{s.icon}</span>
                        <div>
                          <h3 className="font-extrabold text-foreground mb-1 group-hover:text-primary transition-colors text-sm">
                            {s.title}
                          </h3>
                          <p className="text-xs text-muted-foreground">{s.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* FAQ */}
                <div>
                  <h2 className="text-2xl font-extrabold text-foreground mb-6">
                    Veelgestelde Vragen — {loc.city}
                  </h2>
                  <div className="space-y-4">
                    {loc.faqs.map((faq) => (
                      <div key={faq.q} className="rc-card-soft p-6 lg:p-7">
                        <h3 className="rc-h3 mb-2">{faq.q}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Right: sticky sidebar (1/3) — lazy loaded ── */}
              <aside className="lg:col-span-1 space-y-6">
                <div className="sticky top-6 space-y-6">

                  {/* Quick contact card — phone primary, offerte secondary */}
                  <div className="bg-primary rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-extrabold bg-accent text-foreground px-2 py-0.5 rounded-full">
                        🔴 Diagnose vandaag
                      </span>
                    </div>
                    <h3 className="font-extrabold text-lg mb-2 mt-2">Snel Geolpen?</h3>
                    <p className="text-white/80 text-sm mb-4">
                      Bel ons voor een afspraak vandaag. Wij plannen u dezelfde dag in.
                    </p>
                    {/* PRIMARY: Phone */}
                    <a
                      href="tel:0498545474"
                      className="block w-full text-center bg-accent text-foreground px-4 py-3.5 rounded-full font-extrabold text-sm hover:opacity-90 transition-opacity mb-2 shadow-md"
                      onClick={() => trackCityCTAClick('phone', loc.slug, loc.city, 'sidebar')}
                    >
                      📞 BEL: 0498 54 54 74
                    </a>
                    {/* SECONDARY: Offerte */}
                    <Link
                      href="/offerte"
                      className="block w-full text-center bg-white text-primary px-4 py-3 rounded-full font-extrabold text-sm hover:bg-white/90 transition-colors mb-2"
                      onClick={() => {
                        trackCityCTAClick('offerte', loc.slug, loc.city, 'sidebar');
                        trackDiagnosisRequest(loc.slug, loc.city, 'sidebar');
                      }}
                    >
                      📋 Gratis Offerte
                    </Link>
                    {/* TERTIARY: WhatsApp */}
                    <a
                      href={`https://wa.me/32498545474?text=Hallo%2C%20ik%20kom%20uit%20${encodeURIComponent(loc.city)}%20en%20heb%20een%20vraag.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center bg-green-500/80 text-white px-4 py-2.5 rounded-full font-bold text-xs hover:bg-green-500 transition-colors"
                      onClick={() => trackCityCTAClick('whatsapp', loc.slug, loc.city, 'sidebar')}
                    >
                      💬 Stuur een bericht
                    </a>
                    <p className="text-white/50 text-xs text-center mt-3">
                      Dijkstraat 22, 9160 Lokeren
                    </p>
                  </div>

                  {/* Related services sidebar — lazy */}
                  <div className="rc-card p-5 shadow-sm">
                    <h3 className="font-extrabold text-foreground mb-4 text-sm uppercase tracking-wide">
                      Gerelateerde Diensten
                    </h3>
                    <ul className="space-y-1">
                      {relatedServices.map((s) => (
                        <li key={s.href}>
                          <Link
                            href={s.href}
                            className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors py-1.5 border-b border-border/50 last:border-0"
                          >
                            <span className="text-base">{s.icon}</span>
                            <span className="font-medium">{s.label}</span>
                            <span className="ml-auto text-border">›</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Nearby cities sidebar — lazy */}
                  {nearbyCities.length > 0 && (
                    <div className="rc-card p-5 shadow-sm">
                      <h3 className="font-extrabold text-foreground mb-4 text-sm uppercase tracking-wide">
                        Nabijgelegen Steden
                      </h3>
                      <ul className="space-y-1">
                        {nearbyCities.map((nearby) => (
                          <li key={nearby.slug}>
                            <Link
                              href={getCanonicalLocationPath(nearby.slug)}
                              className="flex items-center justify-between text-sm text-muted-foreground hover:text-primary transition-colors py-1.5 border-b border-border/50 last:border-0"
                            >
                              <span className="font-medium">{nearby.city}</span>
                              <span className="text-xs text-muted-foreground/60">{nearby.driveTime}</span>
                            </Link>
                          </li>
                        ))}
                        <li className="pt-2">
                          <Link
                            href="/locaties"
                            className="text-xs text-primary font-semibold hover:underline"
                          >
                            Alle locaties bekijken →
                          </Link>
                        </li>
                      </ul>
                    </div>
                  )}

                  {/* Province context */}
                  <div className="rc-card-soft p-5 lg:p-6">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">{loc.city}</strong> ligt in{' '}
                      <strong className="text-foreground">{loc.province}</strong> en is{' '}
                      <strong className="text-foreground">{loc.driveTime}</strong> rijden van Roetfilter Center in Lokeren via de {loc.highway}.
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* ── Nearby cities strip ── */}
        <section className="rc-section bg-secondary/20">
          <div className="rc-container">
            <h2 className="text-2xl font-extrabold text-foreground mb-6 text-center">
              Roetfilter Reinigen in Uw Regio
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {loc.nearbyCityLinks.map((citySlug) => {
                const nearbyLoc = allLocaties.find((l) => l.slug === citySlug);
                const label = nearbyLoc ? nearbyLoc.city : citySlug;
                return (
                  <Link
                    key={citySlug}
                    href={getCanonicalLocationPath(citySlug)}
                    className="bg-white border border-border px-4 py-2 rounded-full text-sm font-semibold text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                  >
                    {label}
                  </Link>
                );
              })}
              <Link
                href="/locaties"
                className="bg-primary text-white border border-primary px-4 py-2 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Alle locaties →
              </Link>
            </div>
          </div>
        </section>

        {/* ── CTA section — localized urgency ── */}
        <section className="py-16 bg-primary text-white">
          <div className="rc-container max-w-4xl text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">
              🔴 {urgencyLabel}
            </div>
            <h2 className="text-3xl font-extrabold mb-4">
              {loc.cta}
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              Slechts {loc.driveTime} rijden van {loc.city} naar Dijkstraat 22, 9160 Lokeren.
              Gratis diagnose bij herstelling. 1 jaar garantie.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4">
              {/* PRIMARY: Phone */}
              <a
                href="tel:0498545474"
                className="inline-flex items-center justify-center gap-2 bg-accent text-foreground px-8 py-4 rounded-full font-extrabold text-lg hover:opacity-90 transition-opacity shadow-xl"
                onClick={() => trackCityCTAClick('phone', loc.slug, loc.city, 'cta-section')}
              >
                📞 BEL NU: 0498 54 54 74
              </a>
              {/* SECONDARY: Offerte */}
              <Link
                href="/offerte"
                className="inline-flex items-center justify-center gap-2 bg-white text-primary px-8 py-4 rounded-full font-extrabold text-base hover:bg-white/90 transition-colors shadow-lg"
                onClick={() => {
                  trackCityCTAClick('offerte', loc.slug, loc.city, 'cta-section');
                  trackDiagnosisRequest(loc.slug, loc.city, 'city-page');
                }}
              >
                📋 Gratis Offerte Aanvragen
              </Link>
            </div>
            <p className="text-white/60 text-sm mt-6">
              Dijkstraat 22 — 9160 Lokeren | Ma–Vr: 08:00–18:30 | Za: 09:00–17:30 | info@roetfilter-center.be
            </p>
          </div>
        </section>

        {/* ── Master internal linking — dynamically imported ── */}
        <Suspense fallback={<div className="py-14 bg-secondary/10 border-t border-border" />}>
          <SiteInternalLinks variant="city" currentSlug={loc.slug} currentCity={loc.city} />
        </Suspense>
      </main>
      <Footer />
    </>
  );
}
